import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import PostAdminButton from "../Assets/PostAdminButton";
import useDosage from "../Hooks/useDosage";
import ModalConfirmation from "../Assets/ModalConfirmation";
import { DosageEntry } from "../Types/Types";
import AdminSearchBar from "../Assets/AdminSearchBar";
import AdminDosageTable from "./AdminDosageTable";
import DosageModal from "./DosageModal";
import DosageEditModal from "./DosageEditModal";
import { useTranslation } from "react-i18next";

interface AdminDosageProps {
  lang: string;
}

const AdminDosage: React.FC<AdminDosageProps> = ({ lang }) => {
  const { t } = useTranslation()

  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [toDeleteId, setToDeleteId] = useState<string | null>(null);
  const [dosageToEdit, setDosageToEdit] = useState<DosageEntry | null>(null);
  const {
    dosages,
    loading,
    error,
    createDosages,
    deleteDosage,
    updateDosage,
    searchQuery,
    setSearchQuery,
    setSortBy,
    setSortOrder,

  } = useDosage();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleDeleteRequest = (id: string) => {
    setToDeleteId(id);
    setModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (toDeleteId) {
      await deleteDosage(toDeleteId);
      setToDeleteId(null);
    }
    setModalOpen(false);
  };

  const handleDeleteCancel = () => {
    setToDeleteId(null);
    setModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <div className="w-80">
        <SidebarAdmin />
      </div>
      <div className="flex-1 flex flex-col">
        <AdminNavbar />
        <div className="flex-1 py-12 px-48 space-y-12 font-montserrat">
          <h1 className="text-4xl font-bold text-brand-800">
            {t(`admin.dosage`)}
          </h1>
          <div className="flex justify-between items-center space-x-4">
            <PostAdminButton onClick={() => setShowForm(true)} />
            <AdminSearchBar searchQuery={searchQuery} onSearch={handleSearch} />
          </div>
          <AdminDosageTable
            dosageData={dosages}
            loading={loading}
            error={error}
            onDelete={handleDeleteRequest}
            onEdit={(entry) => {
              setDosageToEdit(entry);
              setShowEditModal(true);
            }}
            onSortChange={(field, order) => {
              setSortBy(field);
              if (order === 'asc' || order === 'desc') {
                setSortOrder(order);
              }
            }}
            lang={lang}
          />
        </div>
      </div>

      <DosageModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={async (formData: FormData) => {
          await createDosages(formData);
          setShowForm(false);
        }}
      />
      {showEditModal && (
        <DosageEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={async (id, dosageData) => {
            await updateDosage(id, dosageData);
            setShowEditModal(false);

          }}
          initialData={dosageToEdit!}
          lang={lang}
        />
      )}
      <ModalConfirmation
        isOpen={modalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default AdminDosage;
